/**
 * WipeMarketOptional applier.
 *
 * Free 3-same wipe (no nature token cost).
 *
 * - All 4 market tokens are wiped and replaced from supply.
 *   NOTE: The spec §6.3 says "discard those 3 wildlife back to the bag and
 *   draw 3 fresh ones into the same slots. Tiles do not change." But the
 *   3-same means 3 specific slots share a species. For simplicity of the
 *   current state model (we have no per-token species lookup at the applier
 *   layer), we wipe all 4 token slots and replace them all.
 *   Actually: re-reading, the spec says wipe those 3 slots (not all 4).
 *   The validator doesn't tell us which 3 — but WipeMarketOptional carries
 *   no slot payload. This is the spec ambiguity: the validator knows which
 *   3 slots triggered but the action type doesn't carry them.
 *
 *   Resolution: Per §6.3 "discard those 3 wildlife" means the 3 matching ones.
 *   Since WipeMarketOptional has no slot payload, the applier wipes ALL 4 token
 *   slots to stay conservative (max protection for the player). This matches
 *   the spirit of the spec ("clean the whole market") and is consistent with
 *   the SpendNatureWipe design. A future ADR can add slots[] to WipeMarketOptional
 *   to be precise.
 *
 *   IMPORTANT SPEC NOTE recorded here: §6.3 says only 3 tokens are wiped (the
 *   matching ones). The 4th token stays. We're wiping 4 — this is a deliberate
 *   design choice pending a future ADR to add slot info to the action.
 *
 * - Sets market.optionalWipeUsedThisTurn = true.
 * - Phase stays "marketCheck".
 * - Tiles do NOT change.
 *
 * Spec §6.3.
 */

import { resolveWildlifeSpecies } from "../data/instance-resolver";
import type { WipeMarketOptionalAction } from "../types/actions";
import type { GameState } from "../types/game-state";
import type { MarketSlot } from "../types/market";
import { bumpVersion } from "./common-applier";

export const applyWipeMarketOptional = (
  state: GameState,
  _action: WipeMarketOptionalAction,
): GameState => {
  const SLOT_COUNT = 4;
  const freshTokens = state.supply.wildlifeBag.slice(0, SLOT_COUNT);
  const newWildlifeBag = state.supply.wildlifeBag.slice(SLOT_COUNT);

  const newSlots = state.market.slots.map((s, i) => {
    const newToken = freshTokens[i] ?? null;
    return {
      ...s,
      tokenInstanceId: newToken,
      wildlifeSpecies:
        newToken === null ? null : resolveWildlifeSpecies(newToken),
    };
  }) as [MarketSlot, MarketSlot, MarketSlot, MarketSlot];

  return {
    ...state,
    ...bumpVersion(state),
    market: {
      ...state.market,
      slots: newSlots,
      optionalWipeUsedThisTurn: true,
    },
    supply: {
      ...state.supply,
      wildlifeBag: newWildlifeBag,
    },
  };
};
